import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import ResetPasswordModal from '../../src/components/ResetPasswordModal';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { titleCase, formatPhone } from '../../src/utils/format';
import { formatDateTime } from '../../src/utils/timezone';

interface UserItem {
  id: string; accountNumber: string; email: string; name: string;
  phone: string | null; isSuspended: boolean; createdAt: string;
  lastActiveAt: string | null;
}

/** AdminUsers - list, search, suspend, and reset passwords for customer accounts. */
export default function AdminUsers() {
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const router = useRouter();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [creating, setCreating] = useState(false);
  // Edit modal
  const [editItem, setEditItem] = useState<UserItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);
  // Reset password modal
  const [resetTarget, setResetTarget] = useState<UserItem | null>(null);
  // Suspend
  const [confirmSuspend, setConfirmSuspend] = useState<UserItem | null>(null);
  const [suspending, setSuspending] = useState(false);
  // Inline confirm / message
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchUsers = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      const data = await api<{ users: UserItem[]; total: number }>(`/api/admin/users?${params}`);
      if (signal?.aborted) return;
      setUsers(data.users);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  const { search: searchParam } = useLocalSearchParams<{ search?: string }>();
  useEffect(() => {
    const ac = new AbortController();
    const initial = typeof searchParam === 'string' ? searchParam : '';
    if (initial) setSearch(initial);
    fetchUsers(1, initial, ac.signal);
    return () => ac.abort();
  }, [searchParam]);

  const handleSearch = () => fetchUsers(1, search);
  const handlePage = (p: number) => fetchUsers(p, search);

  const handleDeactivate = (userId: string) => {
    setConfirmDeactivate(userId);
  };

  const handleSuspendToggle = async () => {
    if (!confirmSuspend) return;
    setSuspending(true);
    try {
      const action = confirmSuspend.isSuspended ? 'unsuspend' : 'suspend';
      await api(`/api/admin/users/${confirmSuspend.id}/${action}`, { method: 'POST' });
      setConfirmSuspend(null);
      fetchUsers(page, search);
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message });
      setConfirmSuspend(null);
    } finally {
      setSuspending(false);
    }
  };

  const confirmDeactivateUser = async () => {
    if (!confirmDeactivate) return;
    setDeactivating(true);
    try {
      await api(`/api/admin/users/${confirmDeactivate}`, { method: 'DELETE' });
      setConfirmDeactivate(null);
      fetchUsers(page, search);
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message });
      setConfirmDeactivate(null);
    } finally {
      setDeactivating(false);
    }
  };

  const handleCreate = async () => {
    if (!createName || !createEmail || !createPassword) {
      setInlineMsg({ type: 'error', message: 'Name, email, and password are required' });
      return;
    }
    setCreating(true);
    try {
      await api('/api/admin/users', {
        method: 'POST',
        body: { email: createEmail, password: createPassword, name: createName, phone: createPhone || undefined },
      });
      setShowCreate(false);
      setCreateName(''); setCreateEmail(''); setCreatePassword(''); setCreatePhone('');
      fetchUsers(1, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setCreating(false); }
  };

  const openEdit = (user: UserItem) => {
    setEditItem(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditPhone(user.phone || '');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await api(`/api/admin/users/${editItem.id}`, {
        method: 'PUT',
        body: { name: editName, email: editEmail, phone: editPhone },
      });
      setEditItem(null);
      fetchUsers(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (password: string) => {
    if (!resetTarget) return;
    await api(`/api/admin/users/${resetTarget.id}/reset-password`, {
      method: 'POST',
      body: { password },
    });
  };

  const csvEndpoint = `/api/admin/users?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: UserItem }) => (
    <View style={styles.rowWrapper}>
      {/* Whole row navigates to the customer detail; nested action icons keep
          their own onPress (RN nested TouchableOpacity swallows the tap, same
          pattern as admin/owners.tsx rows). */}
      <TouchableOpacity style={styles.row} onPress={() => router.push(`/admin/users/${item.id}` as never)}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{(item.name || '?')[0].toUpperCase()}</Text>
        </View>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={styles.name}>{titleCase(item.name)}</Text>
              {item.isSuspended && (
                <View style={{ backgroundColor: colors.danger, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
                  <Text style={{ fontSize: fs(10), color: colors.white, fontWeight: '700' }}>Suspended</Text>
                </View>
              )}
            </View>
            <Text style={styles.date}>{new Date(item.createdAt).toLocaleDateString()}</Text>
          </View>
          <Text style={styles.meta} numberOfLines={1}>{item.email} · {item.accountNumber}{item.phone ? ` · ${formatPhone(item.phone)}` : ''}</Text>
          <Text style={styles.lastActive} numberOfLines={1}>Last active {item.lastActiveAt ? formatDateTime(item.lastActiveAt) : 'Never'}</Text>
        </View>
        <View style={styles.rowActions}>
          <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
            <Ionicons name="create-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setResetTarget(item)} hitSlop={8}>
            <Ionicons name="key-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setConfirmSuspend(item)} hitSlop={8}>
            <Ionicons name={item.isSuspended ? 'play-circle-outline' : 'pause-circle-outline'} size={18} color={item.isSuspended ? colors.primary : colors.danger} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleDeactivate(item.id)} hitSlop={8}>
            <Ionicons name="trash-outline" size={18} color={colors.danger} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
      <InlineConfirm
        message={`${item.isSuspended ? 'Unsuspend' : 'Suspend'} ${titleCase(item.name)}?`}
        visible={confirmSuspend?.id === item.id}
        onConfirm={handleSuspendToggle}
        onCancel={() => setConfirmSuspend(null)}
        confirmLabel={item.isSuspended ? 'Unsuspend' : 'Suspend'}
        loading={suspending}
        variant={item.isSuspended ? 'default' : 'danger'}
      />
      <InlineConfirm
        message={`Deactivate ${titleCase(item.name)}?`}
        visible={confirmDeactivate === item.id}
        onConfirm={confirmDeactivateUser}
        onCancel={() => setConfirmDeactivate(null)}
        confirmLabel="Deactivate"
        loading={deactivating}
        variant="danger"
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <TouchableOpacity style={styles.createBtn} onPress={() => setShowCreate(true)}>
        <Ionicons name="add" size={20} color={colors.white} />
        <Text style={styles.createText}>Create Customer</Text>
      </TouchableOpacity>

      <InlineMessage
        type={inlineMsg?.type || 'error'}
        message={inlineMsg?.message || ''}
        visible={!!inlineMsg}
        onDismiss={() => setInlineMsg(null)}
      />

      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={handleSearch}
        total={total} page={page} limit={20} onPageChange={handlePage}
        csvEndpoint={csvEndpoint} csvFilename="customers.csv" label="customer"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={users} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No customers found</Text>}
        />
      )}

      {/* Create User Modal */}
      <Modal visible={showCreate} transparent animationType="slide" onRequestClose={() => setShowCreate(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Create Customer</Text>
            <ScrollView nestedScrollEnabled>
              <Text style={styles.fieldLabel}>Name *</Text>
              <TextInput style={styles.fieldInput} placeholder="Full name" placeholderTextColor={colors.textLight}
                value={createName} onChangeText={setCreateName} maxLength={32} />
              <Text style={styles.fieldLabel}>Email *</Text>
              <TextInput style={styles.fieldInput} placeholder="Email" placeholderTextColor={colors.textLight}
                value={createEmail} onChangeText={setCreateEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
              <Text style={styles.fieldLabel}>Password *</Text>
              <TextInput style={styles.fieldInput} placeholder="Password" placeholderTextColor={colors.textLight}
                value={createPassword} onChangeText={setCreatePassword} secureTextEntry maxLength={128} />
              <Text style={styles.fieldLabel}>Phone</Text>
              <TextInput style={styles.fieldInput} placeholder="Phone (optional)" placeholderTextColor={colors.textLight}
                value={createPhone} onChangeText={setCreatePhone} keyboardType="phone-pad" maxLength={20} />
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowCreate(false)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleCreate} disabled={creating}>
                <Text style={styles.saveText}>{creating ? 'Creating...' : 'Create'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit User Modal */}
      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Customer</Text>
            <ScrollView nestedScrollEnabled>
              <Text style={styles.fieldLabel}>Name</Text>
              <TextInput style={styles.fieldInput} value={editName} onChangeText={setEditName} maxLength={32} />
              <Text style={styles.fieldLabel}>Email</Text>
              <TextInput style={styles.fieldInput} value={editEmail} onChangeText={setEditEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
              <Text style={styles.fieldLabel}>Phone</Text>
              <TextInput style={styles.fieldInput} value={editPhone} onChangeText={setEditPhone} keyboardType="phone-pad" maxLength={20} />
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSaveEdit} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        visible={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
        targetName={titleCase(resetTarget?.name) || ''}
      />
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  createBtn: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', gap: 4, backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, marginBottom: spacing.sm },
  createText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  rowWrapper: { borderBottomWidth: 1, borderBottomColor: c.borderLight, paddingVertical: 10 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  avatar: { width: rs(36), height: rs(36), borderRadius: rs(18), backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center' },
  avatarText: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primary },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  date: { fontSize: fontSize.xs, color: c.textLight },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  lastActive: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  rowActions: { flexDirection: 'row', gap: 12 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
